import * as THREE from 'three';
import { FARM } from '../config';
import { applyInteriorDark } from '../engine/interior';
import { nearestOnRay } from './aim';
import { PLANTER_SOIL_Y } from './craftables';
import type { Inventory, ItemId, SeedId, FoodId } from './inventory';

// FARMING v1. A crafted PLANTER deployable takes one crop seed; the crop grows through 3 visual stages
// on the GAME-DAY clock (dayNight.totalDays -- so the L skip-a-day dev key fast-forwards crops too),
// then harvests into food + a seed back, which keeps the loop self-sustaining. Records follow the
// deployable pattern (a rec + a userData backref on the model for aim resolution); the crop visual is
// a child group of the planter model, so it inherits position/yaw and the interior dim for free.

export type CropKind = 'corn' | 'pumpkin' | 'berry';

const CROP_OF_SEED: Record<SeedId, CropKind> = { seed_corn: 'corn', seed_pumpkin: 'pumpkin', seed_berry: 'berry' };
const SEED_OF: Record<CropKind, SeedId> = { corn: 'seed_corn', pumpkin: 'seed_pumpkin', berry: 'seed_berry' };
const FOOD_OF: Record<CropKind, FoodId> = { corn: 'corn', pumpkin: 'pumpkin', berry: 'berries' };
// Planting priority when several seed kinds are carried and none is held: corn first (the strongest
// cooked food of the three), then pumpkin, then berries.
const SEED_ORDER: readonly SeedId[] = ['seed_corn', 'seed_pumpkin', 'seed_berry'];

export function isSeed(id: ItemId | null): id is SeedId { return id === 'seed_corn' || id === 'seed_pumpkin' || id === 'seed_berry'; }

// Eating a RAW crop hands its seed back (you spit the seeds out) -- the bootstrap into farming.
export function seedFromEating(id: ItemId): SeedId | null {
  if (id === 'corn') return 'seed_corn';
  if (id === 'pumpkin') return 'seed_pumpkin';
  if (id === 'berries') return 'seed_berry';
  return null;
}

// Bonus SEED rolls when picking a WILD plant: only the crop's OWN seed (corn -> corn seed, pumpkin -> pumpkin
// seed). Berry seeds NO LONGER ride on every pick -- that made picking a mushroom feel like a berry-seed dispenser.
// Berries instead enter the loop as a rare wild FIND (forageBonusBerries), and eating a berry yields its seed.
export function forageSeedDrops(item: ItemId): Array<{ id: SeedId; amount: number }> {
  const out: Array<{ id: SeedId; amount: number }> = [];
  if ((item === 'corn' || item === 'pumpkin') && Math.random() < FARM.forageSeedChance) out.push({ id: item === 'corn' ? 'seed_corn' : 'seed_pumpkin', amount: 1 });
  return out;
}

// Rare wild berries turned up while foraging the forest floor. Eating them gives berry seeds (seedFromEating),
// so this bootstraps the berry farming loop without a mushroom pick dropping seeds. Returns 0 or 1.
export function forageBonusBerries(): number {
  return Math.random() < FARM.berrySeedChance ? 1 : 0;
}

// Local world save shape for one growing crop (matched back to its planter by position).
export interface SavedPlanter { x: number; z: number; crop: CropKind; plantedDay: number; }

export interface Planter {
  x: number; y: number; z: number; g: THREE.Group;
  crop: CropKind | null;
  plantedDay: number; // dayNight.totalDays at planting
  stage: number;      // -1 empty, 0 sprout, 1 growing, 2 ready (visual model swaps on change)
  cropG: THREE.Group | null;
}

// --- crop stage models --------------------------------------------------------------------------------
// Shared flat-shaded materials (one instance each; planters are few, but the habit is free). All join
// the sealed-base interior dim like every other deployable material.
function cmat(color: number, rough = 0.85): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0, flatShading: true });
  applyInteriorDark(m);
  return m;
}
const M_SPROUT = cmat(0x7fb84a);
const M_STALK = cmat(0x86a83e);
const M_LEAF = cmat(0x5f8f3a);
const M_COB = cmat(0xe6c83a);
const M_PUMPKIN = cmat(0xe0822c, 0.8);
const M_VINE = cmat(0x4f7a34);
const M_BERRY = cmat(0x8a4fb0, 0.6);

function add(g: THREE.Group, geo: THREE.BufferGeometry, m: THREE.Material, x: number, y: number, z: number, ry = 0, rz = 0): THREE.Mesh {
  const e = new THREE.Mesh(geo, m); e.position.set(x, y, z); e.rotation.set(0, ry, rz); e.castShadow = true; g.add(e); return e;
}

// Stage 0 is the same for every crop: a row of small sprouts poking out of the soil.
function makeSprouts(): THREE.Group {
  const g = new THREE.Group();
  for (const [x, z] of [[-0.35, 0.1], [-0.05, -0.12], [0.25, 0.08], [0.45, -0.05]] as const)
    add(g, new THREE.ConeGeometry(0.035, 0.16, 5), M_SPROUT, x, 0.08, z, x * 7, 0.15);
  return g;
}
function makeCorn(stage: 1 | 2): THREE.Group {
  const g = new THREE.Group();
  const h = stage === 1 ? 0.55 : 1.05;
  for (const [x, z] of [[-0.32, 0.08], [0.05, -0.1], [0.38, 0.06]] as const) {
    add(g, new THREE.CylinderGeometry(0.025, 0.04, h, 5), M_STALK, x, h / 2, z);
    for (let i = 0; i < (stage === 1 ? 2 : 4); i++) {
      const leaf = add(g, new THREE.PlaneGeometry(0.34, 0.09), M_LEAF, x, h * 0.3 + i * h * 0.18, z, x * 5 + i * 1.6, 0.5);
      leaf.castShadow = false; // paper-thin leaves shadow as stripes; not worth it
    }
    if (stage === 2) add(g, new THREE.CylinderGeometry(0.045, 0.035, 0.22, 6), M_COB, x + 0.05, h * 0.62, z);
  }
  return g;
}
function makePumpkin(stage: 1 | 2): THREE.Group {
  const g = new THREE.Group();
  for (const [x, z, a] of [[-0.3, 0.05, 0.4], [0.1, -0.08, 2.1], [0.42, 0.1, 4.0]] as const)
    add(g, new THREE.CylinderGeometry(0.015, 0.02, 0.4, 5), M_VINE, x, 0.06, z, a, 1.25); // vines sprawl low across the soil
  const fruit = (x: number, z: number, r: number, m: THREE.Material): void => {
    const s = new THREE.SphereGeometry(r, 8, 6); s.scale(1, 0.75, 1);
    add(g, s, m, x, r * 0.6, z);
  };
  if (stage === 1) { fruit(-0.2, 0.08, 0.09, M_VINE); fruit(0.3, -0.06, 0.07, M_VINE); } // unripe: small + green
  else { fruit(-0.22, 0.08, 0.17, M_PUMPKIN); fruit(0.3, -0.08, 0.14, M_PUMPKIN); }
  return g;
}
function makeBerry(stage: 1 | 2): THREE.Group {
  const g = new THREE.Group();
  const r = stage === 1 ? 0.16 : 0.24;
  for (const [x, z, s] of [[-0.25, 0.05, 1.0], [0.15, -0.08, 0.85], [0.42, 0.1, 0.7]] as const) {
    const bush = new THREE.SphereGeometry(r * s, 7, 5); bush.scale(1, 0.85, 1);
    add(g, bush, M_LEAF, x, r * s * 0.7, z);
    if (stage === 2) for (let i = 0; i < 4; i++) { const a = i * 1.7 + x * 9; add(g, new THREE.SphereGeometry(0.035, 6, 4), M_BERRY, x + Math.cos(a) * r * s * 0.8, r * s * 0.75 + Math.sin(i * 2.4) * 0.05, z + Math.sin(a) * r * s * 0.8); }
  }
  return g;
}
function makeCropModel(kind: CropKind, stage: number): THREE.Group {
  if (stage <= 0) return makeSprouts();
  const s = stage >= 2 ? 2 : 1;
  return kind === 'corn' ? makeCorn(s) : kind === 'pumpkin' ? makePumpkin(s) : makeBerry(s);
}

export class FarmingManager {
  private readonly planters: Planter[] = [];

  // Called by DeployManager.onPlanterPlaced. The model group is owned by the deploy system (collider,
  // interior dim, removal); farming only parents crop visuals into it + tags it for aim resolution.
  register(x: number, y: number, z: number, g: THREE.Group): void {
    const rec: Planter = { x, y, z, g, crop: null, plantedDay: 0, stage: -1, cropG: null };
    this.planters.push(rec);
    g.traverse((o) => { o.userData.planter = rec; });
  }

  // The planter the aim ray hits (crop meshes count -- the userData walk-up finds the rec), else the
  // forgiving centre-tolerance fallback (same pattern as the other functional deployables).
  aimed(rc: THREE.Raycaster): Planter | null {
    if (!this.planters.length) return null;
    for (const h of rc.intersectObjects(this.planters.map((p) => p.g), true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.planter) return o.userData.planter as Planter; o = o.parent; }
    }
    return nearestOnRay(rc, this.planters.map((p) => ({ x: p.x, y: p.y + 0.35, z: p.z, rec: p })), 0.6);
  }

  // The seed to plant: the HELD one wins (use-seed-on-planter), else the first kind carried.
  pickSeed(held: ItemId | null, inv: Inventory): SeedId | null {
    if (isSeed(held) && inv.count(held) > 0) return held;
    for (const s of SEED_ORDER) if (inv.count(s) > 0) return s;
    return null;
  }

  foodOf(kind: CropKind): FoodId { return FOOD_OF[kind]; }

  plant(p: Planter, seed: SeedId, totalDays: number): void {
    if (p.crop) return;
    p.crop = CROP_OF_SEED[seed];
    p.plantedDay = totalDays;
    p.stage = -1; // force the stage-0 model build on the next update
  }

  // 0..100 for the growing label. Growth is LINEAR over stageDays * 2 (stage 2 = ready).
  growthPct(p: Planter, totalDays: number): number {
    if (!p.crop) return 0;
    return Math.min(100, Math.round(((totalDays - p.plantedDay) / (FARM.stageDays * 2)) * 100));
  }
  isReady(p: Planter): boolean { return p.crop !== null && p.stage >= 2; }

  // Harvest a ready crop: clear the planter and hand back food + a seed (the sustainable loop).
  harvest(p: Planter): Array<{ id: ItemId; amount: number }> {
    if (!p.crop || p.stage < 2) return [];
    const kind = p.crop;
    p.crop = null; p.stage = -1;
    if (p.cropG) { p.g.remove(p.cropG); p.cropG = null; }
    return [
      { id: FOOD_OF[kind], amount: FARM.yields[kind] },
      { id: SEED_OF[kind], amount: FARM.seedsBack },
    ];
  }

  // --- LOCAL WORLD SAVE (game/persist.ts). Planter records themselves are re-created by the
  // deployable replay (register()); this restores WHAT is growing in each, matched by position.
  // stage stays -1 so the next update() rebuilds the right stage model from the day clock. ---
  serialize(): SavedPlanter[] {
    return this.planters.filter((p) => p.crop).map((p) => ({ x: p.x, z: p.z, crop: p.crop!, plantedDay: p.plantedDay }));
  }
  deserialize(data: SavedPlanter[]): void {
    for (const s of data) {
      if (s.crop !== 'corn' && s.crop !== 'pumpkin' && s.crop !== 'berry') continue; // stale save data
      const p = this.planters.find((q) => !q.crop && Math.abs(q.x - s.x) < 0.1 && Math.abs(q.z - s.z) < 0.1);
      if (!p) continue;
      p.crop = s.crop; p.plantedDay = s.plantedDay; p.stage = -1;
    }
  }

  // Per-frame: advance growth stages on the game-day clock, swapping the crop model when a stage is
  // reached. Planters number in the handfuls, so the linear walk is free.
  update(totalDays: number): void {
    for (const p of this.planters) {
      if (!p.crop) continue;
      const stage = Math.min(2, Math.floor((totalDays - p.plantedDay) / FARM.stageDays));
      if (stage === p.stage) continue;
      p.stage = stage;
      if (p.cropG) p.g.remove(p.cropG);
      p.cropG = makeCropModel(p.crop, stage);
      p.cropG.position.y = PLANTER_SOIL_Y; // crops grow out of the soil bed
      p.cropG.traverse((o) => { o.userData.planter = p; }); // the crop itself is aim-targetable
      p.g.add(p.cropG);
    }
  }
}
